import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { walletAPI } from '../lib/api';
import { toast } from 'sonner';
import { Lock, Shield, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

// PIN Input Component
const PINInput = ({ value, onChange, length = 4, disabled = false, showPin = false }) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    // Auto-focus first input on mount
    const timer = setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (index, e) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return; // Only allow digits
    
    const newValue = value.split('');
    newValue[index] = val.slice(-1); // Only take last character
    const newPin = newValue.join('');
    onChange(newPin);
    
    // Auto-focus next input
    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pastedData);
  };

  return (
    <div className="flex gap-3 justify-center">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type={showPin ? 'text' : 'password'}
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-14 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none disabled:bg-slate-100"
        />
      ))}
    </div>
  );
};

// PIN Setup Dialog
export function PINSetupDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Enter PIN, 2: Confirm PIN
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const handleSetupPIN = async () => {
    if (pin.length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }
    if (pin !== confirmPin) {
      toast.error('PINs do not match');
      setConfirmPin('');
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      await walletAPI.setupPin(pin, confirmPin);
      toast.success('Wallet PIN set successfully! 🔐');
      onSuccess?.();
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setPin('');
    setConfirmPin('');
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-primary" />
            {step === 1 ? 'Set Up Wallet PIN' : 'Confirm Your PIN'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? 'Create a 4-6 digit PIN to secure your wallet transactions'
              : 'Re-enter your PIN to confirm'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {step === 1 ? (
            <div className="space-y-4">
              <PINInput 
                value={pin} 
                onChange={setPin} 
                length={4} 
                showPin={showPin}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="flex items-center gap-2 mx-auto text-sm text-slate-500 hover:text-slate-700"
              >
                {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showPin ? 'Hide PIN' : 'Show PIN'}
              </button>
              <Button
                onClick={() => pin.length >= 4 && setStep(2)}
                disabled={pin.length < 4}
                className="w-full h-12 mt-4"
              >
                Continue
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <PINInput 
                value={confirmPin} 
                onChange={setConfirmPin} 
                length={4}
                showPin={showPin}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="flex items-center gap-2 mx-auto text-sm text-slate-500 hover:text-slate-700"
              >
                {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showPin ? 'Hide PIN' : 'Show PIN'}
              </button>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => { setStep(1); setConfirmPin(''); }}
                  className="flex-1 h-12"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSetupPIN}
                  disabled={confirmPin.length < 4 || loading}
                  className="flex-1 h-12"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Set PIN'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-800 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            Your PIN will be required for all wallet transactions including payments, transfers, and checkouts.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// PIN Verification Dialog (for transactions)
export function PINVerificationDialog({ open, onOpenChange, onVerified, title = 'Enter PIN to Continue' }) {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const handleVerify = async () => {
    if (pin.length < 4) {
      toast.error('Please enter your 4-digit PIN');
      return;
    }

    setLoading(true);
    try {
      const response = await walletAPI.verifyPin(pin);
      if (response.data.verified) {
        toast.success('PIN verified! ✓');
        onVerified(response.data.token);
        onOpenChange(false);
        setPin('');
        setAttempts(0);
      }
    } catch (error) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPin('');
      
      if (newAttempts >= 3) {
        toast.error('Too many incorrect attempts. Please try again later.');
        onOpenChange(false);
        setAttempts(0);
      } else {
        toast.error(`Incorrect PIN. ${3 - newAttempts} attempts remaining.`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setPin('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Lock className="h-6 w-6 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Enter your 4-digit wallet PIN to authorize this transaction
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          <PINInput 
            value={pin} 
            onChange={setPin} 
            length={4}
            showPin={showPin}
            disabled={loading}
          />
          
          <button
            type="button"
            onClick={() => setShowPin(!showPin)}
            className="flex items-center gap-2 mx-auto text-sm text-slate-500 hover:text-slate-700"
          >
            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPin ? 'Hide PIN' : 'Show PIN'}
          </button>

          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-12"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerify}
              disabled={pin.length < 4 || loading}
              className="flex-1 h-12"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  Verify
                </>
              )}
            </Button>
          </div>
        </div>

        {attempts > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-800">
              {3 - attempts} attempts remaining before temporary lockout
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// PIN Change Dialog
export function PINChangeDialog({ open, onOpenChange, onSuccess }) {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [step, setStep] = useState(1); // 1: Current, 2: New, 3: Confirm
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const handleChangePIN = async () => {
    if (newPin !== confirmNewPin) {
      toast.error('New PINs do not match');
      setConfirmNewPin('');
      return;
    }

    setLoading(true);
    try {
      await walletAPI.changePin(currentPin, newPin, confirmNewPin);
      toast.success('PIN changed successfully! 🔐');
      onSuccess?.();
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change PIN');
      if (error.response?.data?.detail?.includes('Current PIN')) {
        setStep(1);
        setCurrentPin('');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setCurrentPin('');
    setNewPin('');
    setConfirmNewPin('');
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const stepTitles = {
    1: 'Enter Current PIN',
    2: 'Create New PIN',
    3: 'Confirm New PIN'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Lock className="h-6 w-6 text-primary" />
            Change Wallet PIN
          </DialogTitle>
          <DialogDescription>{stepTitles[step]}</DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          {step === 1 && (
            <>
              <PINInput value={currentPin} onChange={setCurrentPin} length={4} showPin={showPin} />
              <Button
                onClick={() => currentPin.length >= 4 && setStep(2)}
                disabled={currentPin.length < 4}
                className="w-full h-12"
              >
                Continue
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <PINInput value={newPin} onChange={setNewPin} length={4} showPin={showPin} />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-12">Back</Button>
                <Button onClick={() => newPin.length >= 4 && setStep(3)} disabled={newPin.length < 4} className="flex-1 h-12">Continue</Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <PINInput value={confirmNewPin} onChange={setConfirmNewPin} length={4} showPin={showPin} />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1 h-12">Back</Button>
                <Button onClick={handleChangePIN} disabled={confirmNewPin.length < 4 || loading} className="flex-1 h-12">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Change PIN'}
                </Button>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={() => setShowPin(!showPin)}
            className="flex items-center gap-2 mx-auto text-sm text-slate-500 hover:text-slate-700"
          >
            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPin ? 'Hide PIN' : 'Show PIN'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to manage PIN state
export function usePINVerification() {
  const [showPINDialog, setShowPINDialog] = useState(false);
  const [pinToken, setPinToken] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const requirePIN = (action) => {
    setPendingAction(() => action);
    setShowPINDialog(true);
  };

  const handlePINVerified = (token) => {
    setPinToken(token);
    if (pendingAction) {
      pendingAction(token);
      setPendingAction(null);
    }
  };

  const clearPINToken = () => {
    setPinToken(null);
  };

  return {
    showPINDialog,
    setShowPINDialog,
    pinToken,
    requirePIN,
    handlePINVerified,
    clearPINToken,
    PINDialog: (
      <PINVerificationDialog
        open={showPINDialog}
        onOpenChange={setShowPINDialog}
        onVerified={handlePINVerified}
      />
    )
  };
}

export default PINVerificationDialog;
